import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { debounce } from 'lodash';
import { Menu, ChevronLeft, ChevronRight, Check, AlertCircle } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { Progress } from '../../components/ui/progress';
import { formatApiErrorDetail } from '../../context/AuthContext';

import WelcomeStep from './steps/WelcomeStep';
import ContactDetailsStep from './steps/ContactDetailsStep';
import PersonalInfoStep from './steps/PersonalInfoStep';
import EmploymentStep from './steps/EmploymentStep';
import InvestmentAccountsStep from './steps/InvestmentAccountsStep';
import OtherAssetsStep from './steps/OtherAssetsStep';
import InsurancePolicyStep from './steps/InsurancePolicyStep';
import LiabilitiesStep from './steps/LiabilitiesStep';
import GoalsStep from './steps/GoalsStep';
import RetirementExpensesStep from './steps/RetirementExpensesStep';
import AnnualTaxesStep from './steps/AnnualTaxesStep';
import AttachmentsStep from './steps/AttachmentsStep';
import ReviewStep from './steps/ReviewStep';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_TITLES = [
  'Welcome',
  'Contact Details',
  'Personal Information',
  'Employment & Income',
  'Investment Accounts',
  'Other Assets',
  'Insurance Policy',
  'Liabilities',
  'Financial Goals',
  'Retirement & Basic Living Expenses',
  'Annual Taxes',
  'Attachments',
  'Review & Submit'
];

/**
 * Required-fields check per step. Returns { required, satisfied }.
 * - Contact Details (step 2): Legal First & Last Name.
 * - Employment & Income (step 4): at least one employer with some info.
 * - All other steps: no required fields (satisfied by default).
 */
function evaluateStep(stepIdx, formData) {
  if (stepIdx === 2) {
    const c = formData.contact || {};
    return {
      required: true,
      satisfied: !!(c.firstName || '').trim() && !!(c.lastName || '').trim(),
    };
  }
  if (stepIdx === 4) {
    const employers = (formData.employment && formData.employment.employers) || [];
    const hasAny = employers.some((e) => e && (
      (e.employer || '').toString().trim() ||
      (e.employmentIncome || '').toString().trim() ||
      (e.totalIncome || '').toString().trim() ||
      (e.liquidNetWorth || '').toString().trim()
    ));
    return { required: true, satisfied: hasAny };
  }
  return { required: false, satisfied: true };
}

export default function ClientForm() {
  const { inviteId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const loadDraft = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/client/draft/${inviteId}`);
      if (data.data) {
        setFormData(data.data);
        setCurrentStep(data.current_step || 1);
      }
    } catch (e) {
      // Draft may not exist yet — treat as empty and let the client start fresh.
      // Log at debug so genuine 5xx failures still surface in DevTools.
      if (e?.response?.status && e.response.status >= 500) {
        console.error('[loadDraft] server error:', e.response.status, e.response.data);
      }
    }
  }, [inviteId]);

  useEffect(() => {
    loadDraft();
  }, [loadDraft]);

  const saveDraftDebounced = useCallback(
    debounce(async (step, data) => {
      setSaving(true);
      try {
        await axios.post(`${API}/client/draft/${inviteId}`, { step, data });
      } catch (e) {
        // Silent draft-save failure — next keystroke retries. Surface real errors.
        console.warn('[saveDraft] transient failure:', e?.response?.status, e?.message);
      } finally {
        setSaving(false);
      }
    }, 1000),
    [inviteId]
  );

  const updateFormData = (section, data) => {
    const newData = { ...formData, [section]: { ...formData[section], ...data } };
    setFormData(newData);
    saveDraftDebounced(currentStep, newData);
  };

  const handleNext = () => {
    if (currentStep < STEP_TITLES.length) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (files.length >= 10) {
      toast.error('Maximum 10 files allowed');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large (max 25MB)');
      return;
    }

    const fd = new FormData();
    fd.append('file', file);

    try {
      const { data } = await axios.post(`${API}/client/upload/${inviteId}`, fd);
      setFiles([...files, data]);
      toast.success('File uploaded');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const handleFileDelete = async (fileId) => {
    try {
      await axios.delete(`${API}/client/upload/${inviteId}/${fileId}`);
      setFiles(files.filter((f) => f.id !== fileId));
      toast.success('File removed');
    } catch (e) {
      toast.error('Failed to remove file');
    }
  };

  const handleSubmit = async () => {
    if (submitting) return;
    // Client-side pre-submit validation to catch missing required fields quickly.
    const contact = formData.contact || {};
    if (!(contact.firstName || '').trim()) {
      toast.error('Legal First Name is required in Contact Details.');
      setCurrentStep(2);
      return;
    }
    if (!(contact.lastName || '').trim()) {
      toast.error('Legal Last Name is required in Contact Details.');
      setCurrentStep(2);
      return;
    }
    const employers = (formData.employment && formData.employment.employers) || [];
    const hasAnyEmployment = employers.some((e) => e && (
      (e.employer || '').toString().trim() ||
      (e.employmentIncome || '').toString().trim() ||
      (e.totalIncome || '').toString().trim() ||
      (e.liquidNetWorth || '').toString().trim()
    ));
    if (!hasAnyEmployment) {
      toast.error('Please add at least one Employment entry in Employment & Income.');
      setCurrentStep(4);
      return;
    }
    setSubmitting(true);
    try {
      await saveDraftDebounced.flush?.();
      saveDraftDebounced.cancel();
      await axios.post(`${API}/client/submit/${inviteId}`, { data: formData });
      toast.success('Form submitted successfully!');
      navigate('/client/thank-you');
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <WelcomeStep />;
      case 2:
        return <ContactDetailsStep formData={formData.contact || {}} updateFormData={(data) => updateFormData('contact', data)} />;
      case 3:
        return <PersonalInfoStep formData={formData.personal || {}} updateFormData={(data) => updateFormData('personal', data)} />;
      case 4:
        return <EmploymentStep formData={formData.employment || {}} updateFormData={(data) => updateFormData('employment', data)} />;
      case 5:
        return <InvestmentAccountsStep formData={formData.investments || {}} updateFormData={(data) => updateFormData('investments', data)} />;
      case 6:
        return <OtherAssetsStep formData={formData.otherAssets || {}} updateFormData={(data) => updateFormData('otherAssets', data)} />;
      case 7:
        return <InsurancePolicyStep formData={formData.insurance || {}} updateFormData={(data) => updateFormData('insurance', data)} />;
      case 8:
        return <LiabilitiesStep formData={formData.liabilitiesSection || {}} updateFormData={(data) => updateFormData('liabilitiesSection', data)} />;
      case 9:
        return <GoalsStep formData={formData.goals || {}} updateFormData={(data) => updateFormData('goals', data)} />;
      case 10:
        return <RetirementExpensesStep formData={formData.retirement || {}} updateFormData={(data) => updateFormData('retirement', data)} />;
      case 11:
        return <AnnualTaxesStep formData={formData.taxes || {}} updateFormData={(data) => updateFormData('taxes', data)} />;
      case 12:
        return <AttachmentsStep files={files} onFileUpload={handleFileUpload} onFileDelete={handleFileDelete} />;
      case 13:
        return <ReviewStep formData={formData} files={files} onSubmit={handleSubmit} submitting={submitting} />;
      default:
        return null;
    }
  };

  const progress = (currentStep / STEP_TITLES.length) * 100;

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-[#0f2444] via-[#1e3a63] to-[#26518c] shadow-lg">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" data-testid="menu-button">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <SheetTitle className="sr-only">Form Progress Navigation</SheetTitle>
              <div className="py-4">
                <h2 className="text-xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Progress
                </h2>
                <div className="space-y-2">
                  {STEP_TITLES.map((title, idx) => {
                    const stepNum = idx + 1;
                    const status = evaluateStep(stepNum, formData);
                    const isCurrent = stepNum === currentStep;
                    const isPast = stepNum < currentStep;
                    const canJump = isPast;
                    const showCheck = status.satisfied && (status.required ? true : isPast);
                    const showWarning = status.required && !status.satisfied;

                    return (
                    <button
                      key={idx}
                      onClick={() => {
                        if (canJump) {
                          setCurrentStep(stepNum);
                          setDrawerOpen(false);
                        }
                      }}
                      disabled={!canJump && !isCurrent}
                      className={`w-full text-left px-4 py-3 rounded-md transition-colors ${
                        isCurrent
                          ? 'bg-[#5b93d6] text-white font-medium'
                          : isPast
                          ? 'bg-[#f8fafc] text-[#0f172a] hover:bg-[#e2e8f0]'
                          : 'bg-white text-[#94a3b8] cursor-not-allowed'
                      }`}
                      data-testid={`step-nav-${stepNum}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                          isCurrent ? 'bg-white/20' : showCheck ? 'bg-green-100 text-green-700' : showWarning ? 'bg-red-100 text-red-700' : 'bg-[#e2e8f0] text-[#475569]'
                        }`}>
                          {showCheck ? <Check className="w-4 h-4" /> : showWarning ? <AlertCircle className="w-3.5 h-3.5" /> : stepNum}
                        </span>
                        <span className="flex-1">{title}</span>
                        {status.required && (
                          showWarning ? (
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isCurrent ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700'}`}
                              data-testid={`step-required-badge-${stepNum}`}
                              title="Required fields missing"
                            >
                              Required
                            </span>
                          ) : (
                            <span
                              className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${isCurrent ? 'bg-white/20 text-white' : 'bg-green-100 text-green-700'}`}
                              data-testid={`step-done-badge-${stepNum}`}
                              title="Required fields complete"
                            >
                              Done
                            </span>
                          )
                        )}
                      </div>
                    </button>
                    );
                  })}
                </div>
                {/* Legend + submit-readiness */}
                <div className="mt-6 rounded-md border border-[#e2e8f0] bg-[#f8fafc] p-3 text-xs text-[#475569]" data-testid="submit-readiness">
                  {(() => {
                    const missing = [2, 4].filter((i) => !evaluateStep(i, formData).satisfied);
                    if (missing.length === 0) {
                      return <span className="text-green-700 font-medium">All required fields complete — you're ready to submit.</span>;
                    }
                    return (
                      <span>
                        <strong className="text-red-700">To submit</strong>, complete required fields in:{' '}
                        {missing.map((i) => STEP_TITLES[i - 1]).join(', ')}.
                      </span>
                    );
                  })()}
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <div className="flex-1 text-center">
            <div className="flex items-center justify-center gap-3">
              <img src="/stpk-logo.jpg" alt="SteelPeak" className="w-8 h-8 rounded-md shadow-sm" data-testid="brand-logo" />
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif', letterSpacing: '0.05em' }}>
                <span style={{ fontWeight: 800 }}>STEELPEAK</span>{' '}
                <span style={{ fontWeight: 300 }}>WEALTH</span>
              </h1>
            </div>
            <p className="text-sm text-white/80" data-testid="current-step-label">
              Step {currentStep} of {STEP_TITLES.length}: {STEP_TITLES[currentStep - 1]}
            </p>
          </div>

          <div className="w-24 text-right">
            {saving && <span className="text-xs text-white/80">Saving...</span>}
          </div>
        </div>

        <Progress value={progress} className="h-1 rounded-none" data-testid="progress-bar" />
      </header>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <div className="bg-white rounded-lg shadow-sm border border-[#e2e8f0] p-8 mb-6">
          {renderStep()}
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className="px-6"
            data-testid="back-button"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          {currentStep < STEP_TITLES.length && (
            <Button
              onClick={handleNext}
              className="bg-[#5b93d6] hover:bg-[#4a7dc5] text-white px-6"
              data-testid="next-button"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
